/**
 * @fileoverview External insolvency appointee detection.
 * @module jobs/summarization/agents/corporate/insolvencyAppointees
 *
 * Purpose:
 * - Identify administrators, liquidators, receivers and controllers from the
 *   role text attached to an officeholder.
 *
 * Administrators and liquidators are appointed OVER the company by creditors or
 * the court. ASIC extracts list them in the same officeholders table as
 * directors and secretaries, so extraction picks them up as officers with
 * `role: "other"`. They are not part of the company's own structure and must
 * not appear as directors/officers or related entities in the corporate
 * summary.
 *
 * Used by:
 * - convex/jobs/summarization/agents/corporate/queryHelpers.ts (summary tab)
 * - src/components/jobs/phases/gather/summarization/corporate/** (structure map,
 *   entities table)
 */

const APPOINTEE_PATTERNS: RegExp[] = [
  /\breceivers?\b/i,
  /\breceivers?\s+and\s+managers?\b/i,
  /\badministrators?\b/i,
  /\badministration\b/i,
  /\bliquidators?\b/i,
  /\bliquidation\b/i,
  /\bcontrollers?\b/i,
  /\bvoluntary\s+admin/i,
  /\bdeed\s+administrator/i,
  /\bprovisional\s+liquidat/i,
  /\bexternal\s+administrat/i,
  /\btrustee\s+in\s+bankruptcy\b/i,
];

/**
 * Test whether any of the supplied role descriptors names an external
 * insolvency appointment.
 *
 * @param values - Role descriptors (e.g. an extraction `role` enum value and
 *   its `roleDescription`). Nullish values are ignored.
 *
 * @example
 * isInsolvencyAppointeeRole("other", "Administrator of a Company under Administration"); // true
 * isInsolvencyAppointeeRole("director"); // false
 */
export function isInsolvencyAppointeeRole(
  ...values: Array<string | null | undefined>
): boolean {
  return values.some(
    (value) => !!value && APPOINTEE_PATTERNS.some((p) => p.test(value)),
  );
}

/**
 * Test whether a `corporateEntries.detail` string describes an external
 * insolvency appointment.
 *
 * Only the leading clause is tested — that is where the agent records the role
 * ("Administrator of a Company under Administration. Appointed 07/08/2026.").
 * Testing the whole string would wrongly exclude a genuine director whose
 * detail mentions the appointment ("Director. Ceased on appointment of
 * liquidators.").
 *
 * @param detail - The entry's free-text detail, if any.
 */
export function isInsolvencyAppointeeDetail(
  detail: string | null | undefined,
): boolean {
  if (!detail) return false;
  const roleClause = detail.split(/[.;\n]/)[0] ?? detail;
  return isInsolvencyAppointeeRole(roleClause);
}
